import { Node, mergeAttributes } from '@tiptap/core';

/**
 * A imagem de uma questão **como nó do documento**, não como texto.
 *
 * Antes, o token (`{{img:<fileId>}}` do que já está salvo, `[](imagens/x.png)`
 * do que acabou de ser anexado) aparecia cru no editor, e havia um painel
 * separado listando os anexos. Isso produzia dois problemas por construção:
 *
 * 1. o autor lia `{{img:9f2c…}}` no meio do enunciado e não tinha como saber
 *    que imagem era;
 * 2. anexo e texto podiam divergir — imagem anexada sem token (upload órfão,
 *    recusado pelo back) ou token sem imagem (a figura some pro aluno). O
 *    painel tinha de **avisar** sobre isso porque o estado permitia.
 *
 * Com a imagem sendo um nó, os dois deixam de ser possíveis: o nó **é** o ponto
 * de uso, e a lista do que subir é derivada do documento. Não há o que
 * sincronizar, e não há aviso a dar.
 *
 * **O formato persistido não muda.** O back guarda o token como texto porque o
 * allowlist de `sanitizeRichText` não tem `img` (e um `<span data-…>` perderia
 * o atributo) — e porque o token, sendo texto, conta como conteúdo visível para
 * `richTextIsEmpty()`, que é o que faz "questão só de imagem" ser um corpo
 * válido. Então a conversão é nas bordas: `tokensToEditorHtml` na entrada,
 * `editorHtmlToTokens` na saída (`question-images.ts`), as duas testadas.
 *
 * `atom` + `inline`: a figura ocupa uma posição no texto, é selecionável por
 * clique e apagável com Delete, e o cursor não entra "dentro" dela.
 */
export const QuestionImage = Node.create({
  name: 'questionImage',
  inline: true,
  group: 'inline',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      /** Imagem já salva. Vira `{{img:<fileId>}}` na serialização. */
      fileId: {
        default: null as string | null,
        parseHTML: (element) => element.getAttribute('data-file-id'),
        renderHTML: (attributes) =>
          attributes.fileId
            ? { 'data-file-id': attributes.fileId as string }
            : {},
      },
      /** Imagem anexada nesta edição. Vira `[](imagens/<filename>)`. */
      filename: {
        default: null as string | null,
        parseHTML: (element) => element.getAttribute('data-filename'),
        renderHTML: (attributes) =>
          attributes.filename
            ? { 'data-filename': attributes.filename as string }
            : {},
      },
      /** Só para exibir dentro do editor — nunca é persistido. */
      src: {
        default: null as string | null,
        parseHTML: (element) => element.getAttribute('src'),
        renderHTML: (attributes) =>
          attributes.src ? { src: attributes.src as string } : {},
      },
      /** Texto do `alt`: o que aparece se a URL não resolver. */
      alt: {
        default: '' as string,
        parseHTML: (element) => element.getAttribute('alt') ?? '',
        renderHTML: (attributes) => ({ alt: (attributes.alt as string) ?? '' }),
      },
    };
  },

  // `data-question-image` é o que distingue esta imagem de um `<img>` qualquer
  // que caia no editor por colagem — aquele é descartado pela sanitização.
  parseHTML() {
    return [{ tag: 'img[data-question-image]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'img',
      mergeAttributes(HTMLAttributes, {
        'data-question-image': '',
        class: 'question-image',
        // As ações não são óbvias num atom node, e não há bubble menu aqui: o
        // `title` é o que responde "e como eu mudo/tiro isso?". A dica
        // dispensável do formulário cobre o arrastar, que é o menos adivinhável.
        title:
          'Arraste para mover. Clique para selecionar — a barra passa a oferecer Trocar e Remover.',
      }),
    ];
  },
});

/** Nome do nó, para `editor.isActive(...)` sem string solta pelos componentes. */
export const QUESTION_IMAGE_NODE = 'questionImage';
